import React, { FC, useEffect, useCallback, useState } from "react";
//import styles from './Layout.module.css';
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";

import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import GroupIcon from "@mui/icons-material/Group";
import ListIcon from "@mui/icons-material/List";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EventIcon from "@mui/icons-material/Event";
import TableChartIcon from "@mui/icons-material/TableChart";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Fade from "@mui/material/Fade";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../store";
import { getTournoi, fetchTournoi } from "../../features/tournoi/tournoiSlice";
import {
  setAdmin,
  admin,
  clearSession,
} from "../../features/session/sessionSlice";
import { Chip, Divider, Menu, MenuItem } from "@mui/material";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, logout } from "../../services/firebaseAuth";
import BarChartIcon from "@mui/icons-material/BarChart";

interface LayoutProps {}

const drawerWidth = 240;
const navItems = [
  { label: "Inscription", path: "/register" },
  { label: "Tableaux", path: "/boards" },
  { label: "Règlement", path: "/rules" },
  { label: "Live", path: "/live", hideOnMobile: true },
  { label: "Joueurs Live", path: "/live-player" },
  { label: "Mes notifications", path: "/player-follow" },
  { label: "Nous contacter", path: "/contactus" },
];

const adminNavItems = [
  {
    label: "Liste des inscrits",
    path: "/admin/registrations",
    icon: <GroupIcon />,
  },
  {
    label: "Liste des inscriptions",
    path: "/admin/registration-list",
    icon: <ListIcon />,
  },
  {
    label: "Inscrits par tableaux",
    path: "/admin/board-registrations",
    icon: <AssignmentIcon />,
  },
  {
    label: "Gérer le tournoi",
    path: "/admin/tournament-managment",
    icon: <EventIcon />,
  },
  {
    label: "Gérer les tableaux",
    path: "/admin/boards-managment",
    icon: <TableChartIcon />,
  },
  {
    label: "Gérer les matchs",
    path: "/admin/matchs",
    icon: <SportsTennisIcon />,
  },
  {
    label: "Pointage",
    path: "/admin/checkin",
    icon: <HowToRegIcon />,
  },
  {
    label: "Statistiques",
    path: "/admin/stats",
    icon: <BarChartIcon />,
  },
  {
    label: "Droits d'administration",
    path: "/admin/admin-rights",
    icon: <AdminPanelSettingsIcon />,
  },
  {
    label: "Gestion des tables",
    path: "/admin/tables-management",
    icon: <TableRestaurantIcon />,
  },
];

const Layout: FC<LayoutProps> = (props: LayoutProps) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(admin);
  const [user] = useAuthState(auth);
  const location = useLocation();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const fetchAdmin = useCallback(async () => {
    await dispatch(fetchTournoi());
    if (!user) return;
    const customClaims = await user.getIdTokenResult();
    const admin = customClaims.claims.admin ? true : (false as boolean);
    dispatch(setAdmin(admin));
  }, [dispatch, user]);

  useEffect(() => {
    fetchAdmin();
  }, [fetchAdmin]);

  // Note: L'initialisation des notifications est maintenant gérée dans PlayerFollowPage
  // pour éviter les problèmes sur iOS

  const tournoi = useAppSelector(getTournoi);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const adminMenu = (
    <>
      <Divider
        orientation="vertical"
        sx={{
          bgcolor: "rgba(255,255,255,0.3)", // semi-transparent blanc
          height: 32,
          alignSelf: "center",
          mx: 2,
          width: "3px", // plus visible
        }}
        flexItem
      />
      <Button
        sx={{ color: "#fff" }}
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        Admin
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#333",
            color: "#fff",
            borderRadius: 2,
            boxShadow: 5,
            minWidth: 200,
          },
        }}
      >
        {adminNavItems.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              navigate(item.path);
              handleClose();
            }}
            sx={{
              "&:hover": {
                backgroundColor: "#555",
              },
              fontWeight: 500,
              fontSize: "0.95rem",
              letterSpacing: 0.5,
            }}
          >
            <ListItemIcon sx={{ color: "#fff", minWidth: 32 }}>
              {item.icon}
            </ListItemIcon>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        textAlign: "center",
        height: "100%",
        backgroundColor: (theme) => theme.palette.primary.main,
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        py: 2,
      }}
    >
      <List>
        {navItems
          .filter((item) => !(item.hideOnMobile && window.innerWidth < 1000))
          .map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "center",
                  color: "#fff",
                  backgroundColor:
                    location.pathname === item.path
                      ? "rgba(255,255,255,0.2)"
                      : "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
                onClick={() => {
                  navigate(item.path);
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "1rem",
                    fontWeight: 500,
                    letterSpacing: 0.5,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        {user ? (
          <ListItem key={"logout"} disablePadding>
            <ListItemButton
              sx={{ textAlign: "center", color: "#fff" }}
              onClick={() => {
                dispatch(clearSession());
                logout();
              }}
            >
              <ListItemText
                primary="Se déconnecter"
                primaryTypographyProps={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  letterSpacing: 0.5,
                }}
              />
            </ListItemButton>
          </ListItem>
        ) : (
          <ListItem key={"login"} disablePadding>
            <ListItemButton
              sx={{ textAlign: "center", color: "#fff" }}
              onClick={() => {
                navigate("/login");
              }}
            >
              <ListItemText
                primary="Se connecter"
                primaryTypographyProps={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  letterSpacing: 0.5,
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
        {isAdmin && (
          <>
            <Divider sx={{ bgcolor: "rgba(255,255,255,0.3)", my: 1 }} />
            <Chip
              label="ADMIN"
              color="secondary"
              variant="outlined"
              sx={{ my: 1 }}
            />
            <List>
              {adminNavItems.map((item) => (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    sx={{
                      justifyContent: "flex-start",
                      textAlign: "center",
                      color: "#fff",
                      backgroundColor:
                        location.pathname === item.path
                          ? "rgba(255,255,255,0.2)"
                          : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                    onClick={() => {
                      navigate(item.path);
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        letterSpacing: 0.5,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <style>
        {`
      @keyframes fadeInDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `}
      </style>
      {!(location.pathname === "/live" && isFullscreen) && (
        <AppBar
          component="nav"
          sx={{ animation: "fadeInDown 0.4s ease-out", py: 1 }}
        >
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: "none" }, color: "#fff" }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.9,
                  transition: "opacity 0.3s ease",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
              }}
              onClick={() => navigate("/")}
              title="Retour à l'accueil"
            >
              <Box
                sx={{
                  backgroundColor: (theme) => theme.palette.background.paper,
                  borderRadius: "8px",
                  p: 0.5,
                  mr: 2,
                  boxShadow: 3,
                }}
              >
                <Box
                  component="img"
                  src="/sqyping-general-01.png"
                  alt="SQYPing Logo"
                  sx={{ height: 40 }}
                />
              </Box>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "#fff", fontWeight: 600, letterSpacing: 1 }}
              >
                {tournoi?.label}
              </Typography>
            </Box>
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              {navItems
                .filter(
                  (item) => !(item.hideOnMobile && window.innerWidth < 900)
                )
                .map((item) => (
                  <Button
                    key={item.label}
                    sx={{
                      color: "#fff",
                      fontWeight:
                        location.pathname === item.path ? "bold" : "normal",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        bottom: -2,
                        height: "2px",
                        width: location.pathname === item.path ? "100%" : "0%",
                        backgroundColor: "#fff",
                        transition: "width 0.3s ease-in-out",
                      },
                      "&:hover::after": {
                        width: "100%",
                      },
                    }}
                    onClick={() => {
                      navigate(item.path);
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              {isAdmin && adminMenu}
              {user ? (
                <Button
                  key={"logout"}
                  sx={{ color: "#fff" }}
                  onClick={() => {
                    dispatch(clearSession());
                    logout();
                  }}
                >
                  {"Se déconnecter"}
                </Button>
              ) : (
                <Button
                  key={"login"}
                  sx={{ color: "#fff" }}
                  onClick={() => {
                    navigate("/login");
                  }}
                >
                  {"Se connecter"}
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      )}
      <Box component="nav" sx={{ display: { xs: "block", md: "none" } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          PaperProps={{
            sx: {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: (theme) => theme.palette.primary.main,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 16,
              borderBottomRightRadius: 16,
              borderBottomLeftRadius: 0,
              color: "#fff",
              py: 2,
            },
          }}
          sx={{
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flex: 1,
          height: 0,
          p: 2,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {tournoi ? <Outlet /> : null}
      </Box>
    </Box>
  );
};

export default Layout;
